import { useEffect, useRef } from "react";
import { getAuthData } from "../../../utils/AuthUtil";
import { is_empty } from "../../Common/utils/common.utils";
import { getLocalStorageItem } from "../../Common/modules/Storage/modules/LocalStorage/utils/LocalStorage.utils";
import { LOCAL_STORAGE_KEYS } from "../../Common/modules/Storage/modules/LocalStorage/constants/LocalStorage.constants";
import { EXLY_SUPPORT_WIDGET_CONSTANTS } from "../constants/ExlySupportWidget.constants";

const ExlySupportWidget = ({
  position = EXLY_SUPPORT_WIDGET_CONSTANTS.DEFAULT_POSITION,
  theme = EXLY_SUPPORT_WIDGET_CONSTANTS.DEFAULT_THEME,
  hideCloseButton = true,
  customCss = null,
}) => {
  const widgetRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const cssLoadedRef = useRef(false);

  useEffect(() => {
    const auth = getAuthData();

    // Check if user is logged in and not staff
    if (is_empty(auth?.email) || is_empty(auth?.sub_domain)) {
      return;
    }

    // Check if support widget is enabled via environment variable
    const isSupportWidgetEnabled =
      !is_empty(process.env.REACT_APP_SUPPORT_WIDGET_ENABLED) &&
      process.env.REACT_APP_SUPPORT_WIDGET_ENABLED === "true";

    if (!isSupportWidgetEnabled) {
      return;
    }

    // Check if user is staff (similar to Frill widget condition)
    const is_staff = !is_empty(
      getLocalStorageItem(LOCAL_STORAGE_KEYS.BD_EMAIL)
    );
    if (is_staff) {
      return;
    }

    // Load CSS first if not already loaded
    const shouldLoadSupportWidgetCss =
      !is_empty(
        process.env[
          EXLY_SUPPORT_WIDGET_CONSTANTS.ENV_VARS.SUPPORT_WIDGET_CSS_URL
        ]
      ) &&
      !cssLoadedRef.current &&
      !document.querySelector(
        EXLY_SUPPORT_WIDGET_CONSTANTS.CSS_SELECTORS.SUPPORT_WIDGET_CSS
      );

    if (shouldLoadSupportWidgetCss) {
      const cssLink = document.createElement("link");
      cssLink.rel = "stylesheet";
      cssLink.href =
        process.env[
          EXLY_SUPPORT_WIDGET_CONSTANTS.ENV_VARS.SUPPORT_WIDGET_CSS_URL
        ];
      cssLink.onload = () => {
        cssLoadedRef.current = true;
        loadWidgetScript(auth);
      };
      cssLink.onerror = () => {
        console.warn("Failed to load support widget CSS");
        // Still try to load the script even if CSS fails
        loadWidgetScript(auth);
      };
      document.head.appendChild(cssLink);
    } else {
      // CSS already loaded or not needed - load script directly
      loadWidgetScript(auth);
    }

    return () => {
      // Cleanup widget when component unmounts
      if (widgetRef.current && widgetRef.current.destroy) {
        widgetRef.current.destroy();
      }
    };
  }, []);

  const loadWidgetScript = (auth) => {
    // Load the support widget script if not already loaded
    const shouldLoadSupportWidgetScript =
      !is_empty(
        process.env[EXLY_SUPPORT_WIDGET_CONSTANTS.ENV_VARS.SUPPORT_WIDGET_URL]
      ) &&
      !scriptLoadedRef.current &&
      !document.querySelector(
        EXLY_SUPPORT_WIDGET_CONSTANTS.CSS_SELECTORS.SUPPORT_WIDGET_SCRIPT
      );

    if (shouldLoadSupportWidgetScript) {
      const script = document.createElement("script");
      script.src =
        process.env[EXLY_SUPPORT_WIDGET_CONSTANTS.ENV_VARS.SUPPORT_WIDGET_URL];
      script.async = true;
      script.defer = true; // Add defer to ensure it loads after DOM is ready
      script.onload = () => {
        scriptLoadedRef.current = true;
        initializeWidget(auth);
      };
      script.onerror = () => {
        console.error("Failed to load support widget script");
      };
      document.head.appendChild(script);
    } else if (scriptLoadedRef.current) {
      initializeWidget(auth);
    }
  };

  const initializeWidget = (auth) => {
    if (widgetRef.current) {
      return;
    }

    // Create the widget element
    const widgetElement = document.createElement(
      EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ELEMENT_TAG
    );

    // Set required attributes
    widgetElement.setAttribute(
      EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ATTRIBUTES.USER_EMAIL,
      auth.email
    );
    widgetElement.setAttribute(
      EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ATTRIBUTES.CLIENT_ID,
      auth.sub_domain || EXLY_SUPPORT_WIDGET_CONSTANTS.DEFAULT_VALUES.CLIENT_ID
    );

    // Set optional attributes
    if (auth.name) {
      widgetElement.setAttribute(
        EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ATTRIBUTES.NAME,
        auth.name
      );
    }
    if (auth.sub_domain) {
      widgetElement.setAttribute(
        EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ATTRIBUTES.SUB_DOMAIN,
        auth.sub_domain
      );
    }
    if (position) {
      widgetElement.setAttribute(
        EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ATTRIBUTES.POSITION,
        position
      );
    }
    if (hideCloseButton) {
      widgetElement.setAttribute(
        EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ATTRIBUTES.HIDE_CLOSE_BUTTON,
        EXLY_SUPPORT_WIDGET_CONSTANTS.DEFAULT_VALUES.HIDE_CLOSE_BUTTON
      );
    }
    if (Object.keys(theme).length > 0) {
      widgetElement.setAttribute(
        EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ATTRIBUTES.THEME,
        JSON.stringify(theme)
      );
    }
    if (customCss) {
      widgetElement.setAttribute(
        EXLY_SUPPORT_WIDGET_CONSTANTS.WIDGET_ATTRIBUTES.CUSTOM_CSS,
        customCss
      );
    }

    // Append to body
    document.body.appendChild(widgetElement);
    widgetRef.current = widgetElement;
  };

  // This component doesn't render anything visible
  return null;
};

export default ExlySupportWidget;
